import React from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    BarChart3,
    Bot,
    CircleDollarSign,
    ChevronRight,
    Film,
    Layers2,
    Network,
    Plane,
    PlugZap,
    ShoppingBag,
    Store,
    Target,
    Users,
    Utensils,
} from "lucide-react";

import "./../LandingPage.css";

const A = "/nextgo-assets";

const industries = [
    {
        number: "1",
        title: "E-Commerce",
        description:
            "Turn every checkout into a new customer acquisition channel.",
        image: require("./../imgs/ecommerce-visual.png"),
        icon: ShoppingBag,
    },
    {
        number: "2",
        title: "Retail",
        description:
            "Make every in-store purchase more relevant, connected, and valuable.",
        image: require("./../imgs/retail-visual.png"),
        icon: Store,
    },
    {
        number: "3",
        title: "Restaurant & QSR Chains",
        description:
            "Transform customer transactions into smarter growth and new revenue.",
        image: require("./../imgs/restaurant-visual.png"),
        icon: Utensils,
    },
    {
        number: "4",
        title: "Travel & Hospitality",
        description:
            "Connect travelers with relevant offers across their journey.",
        image: require("./../imgs/travel-visual.png"),
        icon: Plane,
    },
    {
        number: "5",
        title: "Entertainment",
        description:
            "Turn every ticket, booking, and visit into a new opportunity for engagement.",
        image: require("./../imgs/entertainment-visual.png"),
        icon: Film,
    },
];

function Logo({ footer = false }) {
    return (
        <div className={footer ? "ng-footer-brand" : "ng-logo"}>
            <img
                src={require("./../imgs/nextgo-logo.png")}
                alt="Next Go Logo"
                className="landing-logo-icon"
            />

            <div>
                Next<span>Go</span>
            </div>
        </div>
    );
}

function Feature({ icon: Icon, title, children }) {
    return (
        <div className="ng-feature">
            <div className="ng-feature-icon">
                <Icon />
            </div>

            <div>
                <strong>{title}</strong>
                <span>{children}</span>
            </div>
        </div>
    );
}

export function LandingPage() {
    const navigate = useNavigate();

    return (
        <div className="ng-page">

            {/* =====================================================
          HEADER
      ===================================================== */}

            <header className="ng-header">
                <div className="ng-container ng-header-inner">

                    <a href="#top" aria-label="NextGo home">
                        <Logo />
                    </a>

                    <div className="ng-header-actions">

                        <a
                            className="ng-login"
                            href="https://nextgo.myloyaltea.com/login"
                        >
                            Login
                        </a>

                        <a
                            className="ng-btn ng-btn-primary"
                            href="https://nextgo.myloyaltea.com/interactive-demo"
                        >
                            Explore Demo
                        </a>

                    </div>

                </div>
            </header>


            <main id="top">

                {/* =====================================================
            HERO SECTION
        ===================================================== */}

                <section className="ng-hero">

                    <div className="ng-container ng-hero-grid">

                        <div className="ng-hero-content">

                            <h1>
                                Commerce Media,
                                <br />

                                <span className="ng-blue-text">
                                    Made Simple.
                                </span>
                            </h1>

                            <p className="ng-hero-copy">
                                One infrastructure to launch your own commerce
                                media network, earn placement and ad revenue from third-party
                                brands, and reach high-intent customers across
                                multiple commerce media networks.
                            </p>

                            <div className="ng-actions">

                                <a
                                    className="ng-btn ng-btn-primary"
                                    href="https://nextgo.myloyaltea.com/interactive-demo"
                                >
                                    Explore Demo

                                    <ArrowRight size={18} />
                                </a>

                                <a
                                    className="ng-btn ng-btn-outline"
                                    href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo"
                                >
                                    Talk to an expert
                                </a>

                            </div>

                        </div>


                        <div className="ng-visual">

                            <img
                                src={require("./../imgs/hero-visual.png")}
                                alt="NextGo commerce media experience"
                            />

                        </div>

                    </div>

                </section>


                {/* =====================================================
            PRODUCT SECTION
        ===================================================== */}

                <section
                    className="ng-section ng-section-light"
                    id="products"
                >

                    <div className="ng-container">

                        <div className="ng-center-head">

                            <div className="ng-eyebrow">
                                One platform. Two ways to grow.
                            </div>

                            <h2>
                                The end-to-end infrastructure for what’s next
                                in commerce media.
                            </h2>

                            <p>
                                Whether you want to launch your own commerce
                                media network or reach high-intent customers
                                across multiple networks, NextGo gives you
                                the infrastructure to do both.
                            </p>

                        </div>


                        <div className="ng-product-grid">

                            {/* =================================================
                  COMMERCE PLUS
              ================================================= */}

                            <article className="ng-product-row">

                                <div className="ng-product-copy">

                                    <span className="ng-kicker">
                                        For commerce partners
                                    </span>

                                    <h3>
                                        NextGo{" "}
                                        <span className="ng-blue-text">
                                            Commerce Plus
                                        </span>
                                    </h3>

                                    <p>
                                        Turn your customer traffic into a new
                                        revenue channel. Give third-party brands
                                        access to your customers through relevant,
                                        non-endemic product placement and advertising — without building
                                        the infrastructure yourself.
                                    </p>


                                    <div className="ng-feature-list">

                                        <Feature
                                            icon={Store}
                                            title="Your own commerce media network"
                                        >
                                            Create and operate a commerce media
                                            network under your own brand.
                                        </Feature>

                                        <Feature
                                            icon={CircleDollarSign}
                                            title="Earn ad and placement revenue"
                                        >
                                            Let third-party brands reach your
                                            customers and earn revenue from the
                                            advertising activity.
                                        </Feature>

                                        <Feature
                                            icon={PlugZap}
                                            title="Plug-and-play infrastructure"
                                        >
                                            Launch without building your own
                                            ad-serving, campaign management,
                                            targeting, and attribution infrastructure.
                                        </Feature>

                                    </div>


                                    <a
                                        className="ng-btn ng-btn-primary ng-btn-wide"
                                        href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo%20Commerce%20Plus"
                                    >
                                        Connect with an expert
                                    </a>

                                </div>


                                <div className="ng-product-art">

                                    <img
                                        src={require("./../imgs/commerce-plus-visual.png")}
                                        alt="Commerce Plus product interface"
                                    />

                                </div>

                            </article>


                            <article className="ng-product-row-phone">

                                <div className="ng-product-copy">

                                    <span className="ng-kicker">
                                        For commerce partners
                                    </span>

                                    <h3>
                                        NextGo{" "}
                                        <span className="ng-blue-text">
                                            Commerce Plus
                                        </span>
                                    </h3>

                                    <p>
                                        Turn your customer traffic into a new
                                        revenue channel. Give third-party brands
                                        access to your customers through relevant,
                                        non-endemic product placement and advertising — without building
                                        the infrastructure yourself.
                                    </p>

                                    <div className="ng-product-art">

                                        <img
                                            src={require("./../imgs/commerce-plus-visual.png")}
                                            alt="Commerce Plus product interface"
                                        />

                                    </div>

                                    <div className="ng-feature-list">

                                        <Feature
                                            icon={Store}
                                            title="Your own commerce media network"
                                        >
                                            Create and operate a commerce media
                                            network under your own brand.
                                        </Feature>

                                        <Feature
                                            icon={CircleDollarSign}
                                            title="Earn ad and placement revenue"
                                        >
                                            Let third-party brands reach your
                                            customers and earn revenue from the
                                            advertising activity.
                                        </Feature>

                                        <Feature
                                            icon={PlugZap}
                                            title="Plug-and-play infrastructure"
                                        >
                                            Launch without building your own
                                            ad-serving, campaign management,
                                            targeting, and attribution infrastructure.
                                        </Feature>

                                    </div>


                                    <a
                                        className="ng-btn ng-btn-primary ng-btn-wide"
                                        href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo%20Commerce%20Plus"
                                    >
                                        Connect with an expert
                                    </a>

                                </div>


                            </article>


                            {/* =================================================
                  NEXTGO ADS
              ================================================= */}

                            <article className="ng-product-row reverse">

                                <div className="ng-product-copy">

                                    <span className="ng-kicker">
                                        For brands and advertisers
                                    </span>

                                    <h3>
                                        NextGo{" "}
                                        <span className="ng-blue-text">
                                            Ads
                                        </span>
                                    </h3>

                                    <p>
                                        Access multiple commerce media networks with one integration.
                                        Reach relevant customers
                                        making purchases and turn every
                                        transaction into an opportunity for
                                        customer acquisition.
                                    </p>


                                    <div className="ng-feature-list">

                                        <Feature
                                            icon={Network}
                                            title="One platform, multiple networks"
                                        >
                                            Access commerce media networks across
                                            e-commerce, retail, travel, restaurant
                                            chains, and more.
                                        </Feature>

                                        <Feature
                                            icon={Bot}
                                            title="Remove guesswork with AI-driven targeting"
                                        >
                                            Stay one step ahead with proactive,
                                            AI-powered audience insights and targeting
                                            decisions.
                                        </Feature>

                                        <Feature
                                            icon={Users}
                                            title="High-intent customer acquisition"
                                        >
                                            Connect with relevant customers at
                                            checkout and other purchase moments.
                                        </Feature>

                                    </div>


                                    <a
                                        className="ng-btn ng-btn-primary ng-btn-wide"
                                        href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo%20Ads"
                                    >
                                        Connect with an expert
                                    </a>

                                </div>


                                <div className="ng-product-art">

                                    <img
                                        src={require("./../imgs/ads-visual.png")}
                                        alt="NextGo Ads offer creative"
                                    />

                                </div>

                            </article>

                            <article className="ng-product-row-phone reverse">

                                <div className="ng-product-copy">

                                    <span className="ng-kicker">
                                        For brands and advertisers
                                    </span>

                                    <h3>
                                        NextGo{" "}
                                        <span className="ng-blue-text">
                                            Ads
                                        </span>
                                    </h3>

                                    <p>
                                        Access multiple commerce media networks with one integration.
                                        Reach relevant customers
                                        making purchases and turn every
                                        transaction into an opportunity for
                                        customer acquisition.
                                    </p>

                                    <div className="ng-product-art">

                                        <img
                                            src={require("./../imgs/ads-visual.png")}
                                            alt="NextGo Ads offer creative"
                                        />

                                    </div>


                                    <div className="ng-feature-list">

                                        <Feature
                                            icon={Network}
                                            title="One platform, multiple networks"
                                        >
                                            Access commerce media networks across
                                            e-commerce, retail, travel, restaurant
                                            chains, and more.
                                        </Feature>

                                        <Feature
                                            icon={Bot}
                                            title="Remove guesswork with AI-driven targeting"
                                        >
                                            Stay one step ahead with proactive,
                                            AI-powered audience insights and targeting
                                            decisions.
                                        </Feature>

                                        <Feature
                                            icon={Users}
                                            title="High-intent customer acquisition"
                                        >
                                            Connect with relevant customers at
                                            checkout and other purchase moments.
                                        </Feature>

                                    </div>


                                    <a
                                        className="ng-btn ng-btn-primary ng-btn-wide"
                                        href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo%20Ads"
                                    >
                                        Connect with an expert
                                    </a>

                                </div>

                            </article>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            RELEVANCE SECTION
        ===================================================== */}

                <section
                    className="ng-section ng-section-dark ng-relevance"
                >

                    <div className="ng-container">

                        <div className="ng-center-head">

                            <div
                                className="ng-eyebrow"
                                style={{ color: "#69a9ff" }}
                            >
                                The NextGo advantage
                            </div>

                            <h2 className="ng-dark-title">
                                Relevance is everything.
                            </h2>

                            <p>
                                Turn purchase context and secure first-party
                                data into smarter offers, better customer
                                experiences, and more meaningful connections
                                across the network.
                            </p>

                        </div>


                        <div className="ng-relevance-visual">

                            <img
                                src={require("./../imgs/relevance-visual.png")}
                                alt="Relevant offer based on customer context"
                            />

                        </div>


                        <div className="ng-relevance-content">

                            <div
                                className="ng-eyebrow"
                                style={{ color: "#aaaeb5" }}
                            >
                                Built around relevance
                            </div>

                            <h3 className="ng-ways-title">
                                Four ways to make every touchpoint count.
                            </h3>


                            <div className="ng-ways-grid">

                                {/* Way 1 */}

                                <div className="ng-way">

                                    <div className="ng-way-heading">

                                        <Target className="ng-way-icon" />

                                        <span className="ng-way-tag">
                                            For brands and advertisers
                                        </span>

                                    </div>

                                    <h3>
                                        The right value
                                    </h3>

                                    <p>
                                        Match customers with personlized products and offers based on their
                                        purchase history across the network.
                                    </p>

                                </div>


                                {/* Way 2 */}

                                <div className="ng-way">

                                    <div className="ng-way-heading">

                                        <Network className="ng-way-icon" />

                                        <span className="ng-way-tag">
                                            For brands and advertisers
                                        </span>

                                    </div>

                                    <h3>
                                        The right intent
                                    </h3>

                                    <p>
                                        Use secure first-party data to improve
                                        relevance and reduce duplicate offers.
                                    </p>

                                </div>


                                {/* Way 3 */}

                                <div className="ng-way">

                                    <div className="ng-way-heading">

                                        <Users className="ng-way-icon" />

                                        <span className="ng-way-tag">
                                            For commerce partners
                                        </span>

                                    </div>

                                    <h3>
                                        The right experience
                                    </h3>

                                    <p>
                                        Give customers relevant third-party products and offers that
                                        match what they are buying.
                                    </p>

                                </div>


                                {/* Way 4 */}

                                <div className="ng-way">

                                    <div className="ng-way-heading">

                                        <BarChart3 className="ng-way-icon" />

                                        <span className="ng-way-tag">
                                            For commerce partners
                                        </span>

                                    </div>

                                    <h3>
                                        The right insight
                                    </h3>

                                    <p>
                                        Let our secure, AI-driven data engine
                                        uncover actionable insights from your
                                        first-party data.
                                    </p>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            HOW IT WORKS
        ===================================================== */}

                <section
                    className="ng-section ng-how"
                    id="how-it-works"
                >

                    <div className="ng-container">

                        <div className="ng-center-head">

                            <div className="ng-eyebrow">
                                How it works
                            </div>

                            <h2>
                                A more{" "}
                                <span className="ng-blue-text">
                                    connected
                                </span>{" "}
                                commerce ecosystem.
                            </h2>

                            <p>
                                NextGo connects commerce partners, brands,
                                and customers through a single infrastructure
                                layer, turning every purchase into a growth
                                opportunity.
                            </p>

                        </div>


                        <div className="ng-how-diagram">

                            <img
                                src={require("./../imgs/how-it-works-visual.png")}
                                alt="NextGo infrastructure flow diagram"
                            />

                        </div>


                        <div className="ng-steps">

                            {/* Step 1 */}

                            <div className="ng-step">

                                <div>

                                    <span className="ng-step-number">
                                        1
                                    </span>

                                    <span className="ng-step-label">
                                        INPUT
                                    </span>

                                </div>

                                <hr />

                                <p>
                                    Commerce partners and brands bring data,
                                    products, offers, and campaigns into the network.
                                </p>

                            </div>


                            {/* Step 2 */}

                            <div className="ng-step">

                                <div>

                                    <span className="ng-step-number">
                                        2
                                    </span>

                                    <span className="ng-step-label">
                                        INTELLIGENCE
                                    </span>

                                </div>

                                <hr />

                                <p>
                                    NextGo’s infrastructure matches the right
                                    products and offers with the right customers in real time.
                                </p>

                            </div>


                            {/* Step 3 */}

                            <div className="ng-step">

                                <div>

                                    <span className="ng-step-number">
                                        3
                                    </span>

                                    <span className="ng-step-label">
                                        OUTCOME
                                    </span>

                                </div>

                                <hr />

                                <p>
                                    The most relevant product or offer is delivered at the
                                    right moment, creating value for everyone.
                                </p>

                            </div>

                        </div>


                        <div
                            className="ng-step-actions"
                            id="demo"
                        >

                            <a
                                className="ng-btn ng-btn-primary"
                                href="https://nextgo.myloyaltea.com/interactive-demo"
                            >
                                Explore our interactive demo

                                <ArrowRight size={18} />
                            </a>

                            {/* <a
                                className="ng-btn ng-btn-outline"
                                href="#data"
                            >
                                See how we use first-party data securely
                            </a> */}

                        </div>

                    </div>

                </section>


                {/* =====================================================
            INDUSTRIES SECTION
        ===================================================== */}

                <section
                    className="ng-section ng-section-light"
                    id="industries"
                >

                    <div className="ng-container">

                        <div className="ng-center-head ng-industries-head">

                            <div className="ng-eyebrow">
                                Industries
                            </div>

                            <h2>
                                Built for modern commerce.
                            </h2>

                            <p>
                                NextGo powers commerce media across every
                                commerce experience, from online shopping
                                to in-store purchases.
                            </p>

                        </div>


                        <div className="ng-industries-grid">

                            {industries.map(
                                ({
                                    number,
                                    title,
                                    description,
                                    image,
                                    icon: Icon,
                                }) => (
                                    <article
                                        className="ng-industry"
                                        key={title}
                                    >

                                        <div className="ng-industry-content">

                                            <div className="ng-industry-number">
                                                {number}
                                            </div>

                                            <h3>
                                                {title}
                                            </h3>

                                            <p>
                                                {description}
                                            </p>

                                        </div>


                                        {image && (
                                            <img
                                                className="ng-industry-image"
                                                src={image}
                                                alt={`${title} use case`}
                                            />
                                        )}

                                    </article>
                                )
                            )}

                        </div>

                    </div>

                </section>

            </main>


            {/* =====================================================
          FINAL CTA + FOOTER
      ===================================================== */}

            <footer
                className="ng-section ng-section-dark ng-final"
                id="contact"
            >

                <div className="ng-container">

                    <div
                        className="ng-eyebrow"
                        style={{ color: "#c7c9cd" }}
                    >
                        The next step
                    </div>

                    <h2>
                        Make every purchase count.
                    </h2>

                    <p>
                        Connect your business for a smarter commerce
                        experience
                    </p>


                    <div className="ng-final-actions">

                        <a
                            className="ng-btn ng-btn-light"
                            href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo"
                        >
                            Contact Us
                        </a>

                        <a
                            className="ng-btn ng-btn-dark-outline"
                            href="https://nextgo.myloyaltea.com/interactive-demo"
                        >
                            Explore Demo
                        </a>

                    </div>


                    {/* =================================================
              FOOTER CONTENT
          ================================================= */}

                    <div className="ng-footer">

                        <div className="ng-footer-audience">
                            Built for Commerce Partners | Brands &amp;
                            Advertisers
                        </div>


                        <div className="ng-footer-grid">

                            {/* Products */}

                            <div>

                                <div className="ng-footer-label">
                                    PRODUCTS
                                </div>

                                <div className="ng-footer-links">

                                    <a href="#products">
                                        NextGo Commerce Plus
                                    </a>

                                    <a href="#products">
                                        NextGo Ads
                                    </a>

                                </div>


                                <div
                                    className="ng-footer-label"
                                    style={{ marginTop: 40 }}
                                >
                                    EXPLORE
                                </div>

                                <div className="ng-footer-links">

                                    <a href="https://nextgo.myloyaltea.com/interactive-demo">
                                        Interactive Demo
                                    </a>

                                </div>

                            </div>


                            {/* Industries */}

                            <div>

                                <div className="ng-footer-label">
                                    INDUSTRIES
                                </div>

                                <div className="ng-footer-links">

                                    <a href="#industries">
                                        E-Commerce
                                    </a>

                                    <a href="#industries">
                                        Retail
                                    </a>

                                    <a href="#industries">
                                        Restaurant &amp; QSR Chains
                                    </a>

                                    <a href="#industries">
                                        Travel &amp; Hospitality
                                    </a>

                                    <a href="#industries">
                                        Entertainment
                                    </a>

                                </div>

                            </div>


                            {/* Resources */}

                            <div>

                                <div className="ng-footer-label">
                                    RESOURCES
                                </div>

                                <div className="ng-footer-links">

                                    <a href="#how-it-works">
                                        How It Works
                                    </a>

                                    {/* <a href="#data">
                                        First-Party Data Use
                                    </a> */}

                                    <a href="mailto:contact@myloyaltea.com?subject=Hi%2C%20tell%20me%20more%20about%20NextGo">
                                        Talk to an Expert
                                    </a>

                                </div>

                            </div>

                        </div>


                        {/* Footer Logo */}

                        <Logo footer />


                        {/* Copyright */}

                        <div className="ng-copyright">

                            <span>
                                © NextGo 2026
                            </span>

                            <span>
                                All rights reserved
                            </span>

                        </div>

                    </div>

                </div>

            </footer>

        </div>
    );
}